import React, { useState, useContext, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { GatewayContext } from '../App';
import api, { getImageUrl } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import './Cart.css';

function PayPalLoader() {
  const [{ isPending }] = usePayPalScriptReducer();
  if (!isPending) return null;
  return <div className="paypal-loading">Loading PayPal...</div>;
}

// ── Stripe Payment Form ───────────────────────────────────────────────────────
function StripeForm({ onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setLoading(true);
    console.log('[PAYMENT] Stripe: confirming payment...');
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/cart',
      },
      redirect: 'if_required',
    });
    setLoading(false);
    if (error) { console.error('[PAYMENT] Stripe error:', error.message); onError(error.message); }
    else if (paymentIntent?.status === 'succeeded') {
      console.log('[PAYMENT] Stripe succeeded:', { id: paymentIntent.id, amount: paymentIntent.amount, currency: paymentIntent.currency });
      onSuccess({ orderID: paymentIntent.id, amount: (paymentIntent.amount / 100).toFixed(2), currency: paymentIntent.currency.toUpperCase(), status: 'COMPLETED' });
    } else {
      console.warn('[PAYMENT] Stripe: unexpected status', paymentIntent?.status);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setReady(true)} />
      <button type="submit" className="btn checkout-btn" disabled={!stripe || loading || !ready} style={{ marginTop: 16, width: '100%' }}>
        {loading ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
}

// ── Step 1: Mobile/Email OTP Login ───────────────────────────────────────────
function LoginStep({ onLogin }) {
  const [subStep, setSubStep] = useState('contact'); // 'contact' | 'otp' | 'name'
  const [contactType, setContactType] = useState('mobile'); // 'mobile' | 'email'
  const [loginMode, setLoginMode] = useState('both'); // 'both' | 'sms_only' | 'email_only'
  const [countryCode, setCountryCode] = useState('+353');
  const [mobile, setMobile]   = useState('');
  const [countryCodes, setCountryCodes] = useState([]);

  // Load country codes and login mode from admin
  React.useEffect(() => {
    api.get('/content/country_codes').then(({ data }) => {
      if (data?.codes?.length) {
        setCountryCodes(data.codes);
        setCountryCode(data.defaultCode || data.codes[0]?.code || '+353');
      }
    }).catch(() => {});
    api.get('/auth-settings').then(({ data }) => {
      const mode = data?.loginMode || 'both';
      setLoginMode(mode);
      if (mode === 'email_only') setContactType('email');
      else if (mode === 'sms_only') setContactType('mobile');
    }).catch(() => {});
  }, []);
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState(['', '', '', '']);
  const [name, setName]       = useState('');
  const [isNew, setIsNew]     = useState(false);
  const [devOtp, setDevOtp]   = useState(''); // eslint-disable-line no-unused-vars
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRef0 = React.useRef(null);
  const otpRef1 = React.useRef(null);
  const otpRef2 = React.useRef(null);
  const otpRef3 = React.useRef(null);
  const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3];

  // Countdown timer for resend
  React.useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fullMobile = countryCode + mobile;
      const payload = contactType === 'mobile' ? { mobile: fullMobile } : { email };
      console.log('[AUTH] Sending OTP:', { contactType, payload });
      const { data } = await api.post('/user/send-otp', payload);
      console.log('[AUTH] OTP sent successfully:', { isNew: data.isNew });
      setIsNew(data.isNew);
      setSubStep('otp');
      setResendTimer(60);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      console.error('[AUTH] Send OTP failed:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    }
    setLoading(false);
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 3) otpRefs[i + 1].current?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      otpRefs[3].current?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 4) { setError('Please enter the full 4-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      if (isNew && !name) {
        setSubStep('name');
        setLoading(false);
        return;
      }
      const fullMobile = countryCode + mobile;
      const payload = contactType === 'mobile' 
        ? { mobile: fullMobile, otp: code, name: isNew ? name : undefined }
        : { email, otp: code, name: isNew ? name : undefined };
      console.log('[AUTH] Verifying OTP:', { contactType, isNew });
      const { data } = await api.post('/user/verify-otp', payload);
      console.log('[AUTH] OTP verified, user logged in:', { id: data.user?._id, name: data.user?.name });
      onLogin(data.user);
    } catch (err) {
      console.error('[AUTH] Verify OTP failed:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    }
    setLoading(false);
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    setError('');
    setLoading(true);
    try {
      const code = otp.join('');
      const fullMobile = countryCode + mobile;
      const payload = contactType === 'mobile'
        ? { mobile: fullMobile, otp: code, name }
        : { email, otp: code, name };
      console.log('[AUTH] New user registration:', { contactType, name });
      const { data } = await api.post('/user/verify-otp', payload);
      console.log('[AUTH] User registered & logged in:', { id: data.user?._id, name: data.user?.name });
      onLogin(data.user);
    } catch (err) {
      console.error('[AUTH] Registration failed:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Something went wrong.');
    }
    setLoading(false);
  };

  // ── Contact entry (mobile or email) ──
  if (subStep === 'contact') return (
    <div className="checkout-step">
      <h2>Sign In to Checkout</h2>
      <p className="checkout-step-sub">
        {loginMode === 'email_only' && "Enter your email to receive a one-time code."}
        {loginMode === 'sms_only' && "Enter your mobile number to receive a one-time code."}
        {loginMode === 'both' && "Enter your mobile or email to receive a one-time code."}
      </p>
      
      {/* Toggle between mobile and email - only show if both enabled */}
      {loginMode === 'both' && (
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, background: '#f5f5f5', padding: 4, borderRadius: 8 }}>
        <button
          type="button"
          onClick={() => setContactType('mobile')}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            borderRadius: 6,
            background: contactType === 'mobile' ? '#fff' : 'transparent',
            fontWeight: contactType === 'mobile' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: contactType === 'mobile' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          📱 Mobile
        </button>
        <button
          type="button"
          onClick={() => setContactType('email')}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            borderRadius: 6,
            background: contactType === 'email' ? '#fff' : 'transparent',
            fontWeight: contactType === 'email' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: contactType === 'email' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          ✉️ Email
        </button>
      </div>
      )}

      <form onSubmit={handleSendOtp} className="checkout-form">
        {contactType === 'mobile' ? (
          <div className="checkout-field">
            <label>Mobile Number</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative', width: 130 }}>
                <select
                  value={countryCode}
                  onChange={e => setCountryCode(e.target.value)}
                  style={{ width: '100%', height: 44, border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer', opacity: 0, position: 'absolute', inset: 0, zIndex: 2 }}
                >
                  {countryCodes.map((c, i) => <option key={i} value={c.code}>{c.name} {c.code}</option>)}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 24px 0 10px', height: 44, border: '1.5px solid #ddd', borderRadius: 8, background: '#fff', fontSize: 14, fontWeight: 500, pointerEvents: 'none' }}>
                  <img src={`https://flagcdn.com/24x18/${(countryCodes.find(c => c.code === countryCode)?.flag) || 'ie'}.png`} alt="" style={{ width: 20, height: 15, objectFit: 'cover', borderRadius: 2 }} />
                  <span>{countryCode}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#888' }}>▼</span>
                </div>
              </div>
              <input
                type="tel"
                placeholder="87 123 4567"
                value={mobile}
                onChange={e => setMobile(e.target.value.replace(/^0+/, ''))}
                style={{ flex: 1 }}
                autoFocus
                required
              />
            </div>
            <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>{loginMode === 'sms_only' ? "Enter your mobile number to receive a login code via SMS/WhatsApp" : "You'll receive an OTP via SMS/WhatsApp"}</p>
          </div>
        ) : (
          <div className="checkout-field">
            <label>Email Address</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              required
            />
            <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>{loginMode === 'email_only' ? "Enter your email to receive a login code" : "You'll receive an OTP via email"}</p>
          </div>
        )}
        {error && <p className="checkout-error">{error}</p>}
        <button type="submit" className="btn checkout-btn" disabled={loading}>
          {loading ? 'Sending...' : 'Send Login Code'}
        </button>
      </form>
    </div>
  );

  // ── OTP entry ──
  if (subStep === 'otp') return (
    <div className="checkout-step">
      <h2>Enter Your Code</h2>
      <p className="checkout-step-sub">
        We sent a 4-digit code to <strong>{contactType === 'mobile' ? countryCode + mobile : email}</strong>.
        <button type="button" className="otp-change-email" onClick={() => { setSubStep('contact'); setOtp(['','','','']); setError(''); }}>
          Change
        </button>
      </p>
      <form onSubmit={handleVerifyOtp} className="checkout-form">
        <div className="otp-inputs" onPaste={handleOtpPaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={otpRefs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              className="otp-box"
              autoFocus={i === 0}
            />
          ))}
        </div>
        {error && <p className="checkout-error">{error}</p>}
        <button type="submit" className="btn checkout-btn" disabled={loading || otp.join('').length < 4}>
          {loading ? 'Verifying...' : 'Verify Code'}
        </button>
        <div className="otp-resend">
          {resendTimer > 0
            ? <span>Resend code in {resendTimer}s</span>
            : <button type="button" className="otp-resend-btn" onClick={async () => {
                setError('');
                setOtp(['','','','']);
                setLoading(true);
                try {
                  const fullMobile = countryCode + mobile;
                  const payload = contactType === 'mobile' ? { mobile: fullMobile } : { email };
                  await api.post('/user/send-otp', payload);
                  setResendTimer(60);
                  setTimeout(() => otpRefs[0].current?.focus(), 100);
                } catch (err) { setError('Failed to resend. Please try again.'); }
                setLoading(false);
              }}>Resend Code</button>
          }
        </div>
      </form>
    </div>
  );

  // ── Name entry (new users only) ──
  return (
    <div className="checkout-step">
      <h2>Welcome!</h2>
      <p className="checkout-step-sub">Just one more step — what's your name?</p>
      <form onSubmit={handleNameSubmit} className="checkout-form">
        <div className="checkout-field">
          <label>Full Name</label>
          <input
            placeholder="Your full name"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
            required
          />
        </div>
        {error && <p className="checkout-error">{error}</p>}
        <button type="submit" className="btn checkout-btn" disabled={loading}>
          {loading ? 'Please wait...' : 'Continue to Checkout'}
        </button>
      </form>
    </div>
  );
}

// ── Step 2: Address form ──────────────────────────────────────────────────────
function AddressStep({ user, onSave, onBack }) {
  const { updateUser } = useUser();
  const [form, setForm] = useState({
    name:    user.name || '',
    email:   user.email || '',
    mobile:  user.mobile || '',
    line1:   user.address?.line1 || '',
    city:    user.address?.city || '',
    county:  user.address?.county || '',
    eircode: user.address?.eircode || '',
    country: user.address?.country || 'Ireland',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?._id) { setError('Session expired. Please sign in again.'); return; }
    setLoading(true);
    try {
      const { data } = await api.put(`/user/${user._id}`, {
        name: form.name,
        ...(form.email  ? { email:  form.email  } : {}),
        ...(form.mobile ? { mobile: form.mobile } : {}),
        address: { line1: form.line1, city: form.city, county: form.county, eircode: form.eircode, country: form.country }
      });
      if (!data.user) throw new Error('No user returned');
      updateUser(data.user);
      onSave(data.user);
    } catch (e) { setError(e.response?.data?.error || 'Failed to save address. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="checkout-step">
      <h2>Delivery Details</h2>
      <form onSubmit={handleSubmit} className="checkout-form">
        <div className="checkout-field-row">
          <div className="checkout-field">
            <label>Full Name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div className="checkout-field">
            <label>Mobile</label>
            <input 
              value={form.mobile} 
              onChange={e => set('mobile', e.target.value)}
              style={{ background: user.mobile ? '#f5f5f5' : '#fff' }}
              readOnly={!!user.mobile}
              required
            />
          </div>
        </div>
        <div className="checkout-field">
          <label>Email</label>
          <input 
            type="email" 
            placeholder="your@email.com" 
            value={form.email} 
            onChange={e => set('email', e.target.value)}
            style={{ background: user.email ? '#f5f5f5' : '#fff' }}
            readOnly={!!user.email}
            required
          />
        </div>
        <div className="checkout-field">
          <label>Address Line 1</label>
          <input placeholder="Street address" value={form.line1} onChange={e => set('line1', e.target.value)} required />
        </div>
        <div className="checkout-field-row">
          <div className="checkout-field">
            <label>City / Town</label>
            <input placeholder="Dublin" value={form.city} onChange={e => set('city', e.target.value)} required />
          </div>
          <div className="checkout-field">
            <label>County</label>
            <input placeholder="Dublin" value={form.county} onChange={e => set('county', e.target.value)} required />
          </div>
        </div>
        <div className="checkout-field-row">
          <div className="checkout-field">
            <label>Eircode</label>
            <input placeholder="D01 AB12" value={form.eircode} onChange={e => set('eircode', e.target.value)} required />
          </div>
          <div className="checkout-field">
            <label>Country</label>
            <input value={form.country} onChange={e => set('country', e.target.value)} required />
          </div>
        </div>
        {error && <p className="checkout-error">{error}</p>}
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="btn btn-outline-dark" onClick={onBack}>← Back</button>
          <button type="submit" className="btn checkout-btn" disabled={loading}>
            {loading ? 'Saving...' : 'Continue to Payment'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Payment Step ─────────────────────────────────────────────────────────────
function PaymentStep({
  checkoutUser, cart, subtotal, currency, gateways,
  paypalEnabled, stripeEnabled,
  selectedGateway, setSelectedGateway,
  stripePromise, stripeClientSecret,
  payError, setPayError,
  handleSelectGateway, createPayPalOrder, onPayPalApprove,
  clearCart, setOrderSuccess, setStripeClientSecret,
  onBack, onEditAddress,
  deliveryFee, deliveryChoice, itemSubtotal, deliverySettings,
}) {
  const [billingSame, setBillingSame] = useState(true);
  const [billingForm, setBillingForm] = useState({ name: '', line1: '', city: '', county: '', eircode: '', country: 'Ireland' });

  const gwOptions = [
    paypalEnabled && { id: 'paypal', label: 'PayPal', logos: (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <svg width="60" height="20" viewBox="0 0 60 20" xmlns="http://www.w3.org/2000/svg">
          <text x="0" y="15" fontFamily="Arial" fontWeight="bold" fontSize="14" fill="#003087">Pay</text>
          <text x="24" y="15" fontFamily="Arial" fontWeight="bold" fontSize="14" fill="#009cde">Pal</text>
        </svg>
      </div>
    )},
    stripeEnabled && { id: 'stripe', label: 'Credit / Debit Card', logos: (
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <svg width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg">
          <rect width="38" height="24" rx="4" fill="#1A1F71"/>
          <text x="5" y="17" fontFamily="Arial" fontWeight="bold" fontSize="13" fill="#FFFFFF" letterSpacing="0.5">VISA</text>
        </svg>
        <svg width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg">
          <rect width="38" height="24" rx="4" fill="#252525"/>
          <circle cx="14" cy="12" r="7" fill="#EB001B"/>
          <circle cx="24" cy="12" r="7" fill="#F79E1B"/>
          <path d="M19 6.8a7 7 0 0 1 0 10.4A7 7 0 0 1 19 6.8z" fill="#FF5F00"/>
        </svg>
      </div>
    )},
  ].filter(Boolean);

  const billingAddress = billingSame ? checkoutUser?.address : billingForm;

  const saveOrder = async (result, gateway) => {
    console.log('[ORDER] saveOrder called:', { result, gateway });
    console.log('[ORDER] checkoutUser:', checkoutUser);
    console.log('[ORDER] cart items:', cart.length);
    const fallbackOrderID = result.orderID || `ORD-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (!result.orderID) console.warn('[ORDER] No orderID from payment, using fallback:', fallbackOrderID);
    try {
      const orderPayload = {
        orderID: fallbackOrderID,
        gateway,
        amount: result.amount || subtotal.toFixed(2),
        currency: result.currency || currency || 'EUR',
        customer: {
          name: checkoutUser?.name,
          mobile: checkoutUser?.mobile,
          email: checkoutUser?.email,
          address: checkoutUser?.address,
          billingAddress,
        },
        items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price, image: i.images?.[0] || i.image || '' })),
        delivery: { type: deliveryChoice || 'none', fee: deliveryFee || 0, estimatedDate: new Date(Date.now() + ((deliveryChoice === 'non-registered' ? deliverySettings?.nonRegisteredMaxDays : deliverySettings?.registeredMaxDays) || 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] },
      };
      console.log('[ORDER] Posting to /orders:', orderPayload);
      const { data } = await api.post('/orders', orderPayload);
      console.log('[ORDER] Order created successfully:', data);
      clearCart();
      setOrderSuccess({ ...result, orderID: fallbackOrderID });
      window.scrollTo(0, 0);
    } catch (e) {
      console.error('[ORDER] Failed to create order:', e.response?.data || e.message);
      setPayError('Payment successful but order save failed. Please contact support with ID: ' + fallbackOrderID);
      clearCart();
      setOrderSuccess({ ...result, orderID: fallbackOrderID });
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="cart-page section">
      <div className="container checkout-container">
        <div className="checkout-step">

          {/* Address summary */}
          <div className="checkout-address-summary">
            <strong>{checkoutUser?.name}</strong> · {checkoutUser?.mobile}<br />
            {checkoutUser?.address?.line1}, {checkoutUser?.address?.city}, {checkoutUser?.address?.eircode}
            <button className="checkout-edit-link" onClick={onEditAddress}>Edit</button>
          </div>

          {/* Order summary */}
          <div className="checkout-order-summary">
            {cart.map(item => (
              <div key={item._id} className="checkout-order-item">
                <span>{item.name} × {item.qty}</span>
                <span>{currency}{(item.price * item.qty).toFixed(2)}</span>
              </div>
            ))}
            {deliveryFee > 0 && (
              <div className="checkout-order-item">
                <span>Registered Delivery with An Post</span>
                <span>{currency}{deliveryFee.toFixed(2)}</span>
              </div>
            )}
            {deliveryChoice === "non-registered" && (
              <div className="checkout-order-item">
                <span>Non-Registered Delivery with An Post</span>
                <span className="delivery-free">FREE</span>
              </div>
            )}
            <div className="checkout-order-total">
              <strong>Total</strong><strong>{currency}{subtotal.toFixed(2)}</strong>
            </div>
          </div>

          {/* ── Payment section ── */}
          <div className="payment-section">
            <h2 className="payment-section-title">Payment</h2>
            <p className="payment-section-sub">All transactions are secure and encrypted.</p>

            <div className="payment-options">
              {gwOptions.map((gw, i) => (
                <div
                  key={gw.id}
                  className={`payment-option${selectedGateway === gw.id ? ' selected' : ''}${i === 0 ? ' first' : ''}${i === gwOptions.length - 1 ? ' last' : ''}`}
                  onClick={() => handleSelectGateway(gw.id)}
                >
                  <div className="payment-option-row">
                    <span className="payment-radio">
                      <span className={`payment-radio-dot${selectedGateway === gw.id ? ' active' : ''}`} />
                    </span>
                    <span className="payment-option-label">{gw.label}</span>
                    <span className="payment-option-logos">{gw.logos}</span>
                  </div>

                  {/* Expanded content */}
                  {selectedGateway === gw.id && (
                    <div className="payment-option-body">
                      {gw.id === 'paypal' && (
                        <div className="paypal-btn-wrap" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
                          <PayPalLoader />
                          <PayPalButtons
                            style={{ layout: 'vertical', shape: 'rect', label: 'pay', height: 48 }}
                            createOrder={createPayPalOrder}
                            onApprove={(d) => onPayPalApprove(d).then(() => {}).catch(e => setPayError(e.message))}
                            onError={() => setPayError('Payment failed. Please try again.')}
                            onCancel={() => setPayError('Payment cancelled.')}
                          />
                        </div>
                      )}
                      {gw.id === 'stripe' && stripeClientSecret && stripePromise && (
                        <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                          <StripeForm
                            onSuccess={(result) => saveOrder(result, 'stripe')}
                            onError={(msg) => setPayError(msg)}
                          />
                        </Elements>
                      )}
                      {gw.id === 'stripe' && !stripeClientSecret && (
                        <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Initializing payment...</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {payError && <div className="paypal-error" style={{ marginTop: 12 }}>{payError}</div>}
          </div>

          {/* ── Billing address ── */}
          <div className="billing-section">
            <h3 className="billing-title">Billing address</h3>
            <div className="payment-options">
              <div
                className={`payment-option first${billingSame ? ' selected' : ''}`}
                onClick={() => setBillingSame(true)}
              >
                <div className="payment-option-row">
                  <span className="payment-radio">
                    <span className={`payment-radio-dot${billingSame ? ' active' : ''}`} />
                  </span>
                  <span className="payment-option-label">Same as shipping address</span>
                </div>
              </div>
              <div
                className={`payment-option last${!billingSame ? ' selected' : ''}`}
                onClick={() => setBillingSame(false)}
              >
                <div className="payment-option-row">
                  <span className="payment-radio">
                    <span className={`payment-radio-dot${!billingSame ? ' active' : ''}`} />
                  </span>
                  <span className="payment-option-label">Use a different billing address</span>
                </div>
                {!billingSame && (
                  <div className="payment-option-body">
                    <div className="checkout-form" style={{ gap: 12 }}>
                      <div className="checkout-field">
                        <label>Full Name</label>
                        <input value={billingForm.name} onChange={e => setBillingForm(f => ({ ...f, name: e.target.value }))} placeholder="Name on card" />
                      </div>
                      <div className="checkout-field">
                        <label>Address</label>
                        <input value={billingForm.line1} onChange={e => setBillingForm(f => ({ ...f, line1: e.target.value }))} placeholder="Street address" />
                      </div>
                      <div className="checkout-field-row">
                        <div className="checkout-field">
                          <label>City</label>
                          <input value={billingForm.city} onChange={e => setBillingForm(f => ({ ...f, city: e.target.value }))} placeholder="Dublin" />
                        </div>
                        <div className="checkout-field">
                          <label>Eircode</label>
                          <input value={billingForm.eircode} onChange={e => setBillingForm(f => ({ ...f, eircode: e.target.value }))} placeholder="D01 AB12" />
                        </div>
                      </div>
                      <div className="checkout-field">
                        <label>Country</label>
                        <input value={billingForm.country} onChange={e => setBillingForm(f => ({ ...f, country: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {!paypalEnabled && !stripeEnabled && (
            <div className="paypal-error" style={{ marginTop: 16 }}>No payment methods are currently available. Please contact us.</div>
          )}

          <button className="btn btn-outline-dark" onClick={onBack} style={{ marginTop: 16, width: '100%' }}>← Back</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Cart ─────────────────────────────────────────────────────────────────
function Cart() {
  const { cart, removeFromCart, updateQty, clearCart, subtotal, syncCart, syncNotes, dismissSyncNotes } = useCart();
  const { user, login, logout } = useUser();
  const gateways = useContext(GatewayContext);
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState('cart');
  const [checkoutUser, setCheckoutUser] = useState(user);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [payError, setPayError] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState('');
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState(null);
  const [deliveryChoice, setDeliveryChoice] = useState(null);
  const [showDeliveryPopup, setShowDeliveryPopup] = useState(false);

  const paypalEnabled = gateways?.paypal?.enabled;
  const stripeEnabled = gateways?.stripe?.enabled;

  // Load delivery settings
  useEffect(() => { api.get('/delivery-settings').then(({data}) => setDeliverySettings(data)).catch(() => {}); }, []);

  // Sync cart with live DB prices + stock on mount
  useEffect(() => { syncCart(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-trigger checkout when ?checkout=true (Buy Now flow)
  useEffect(() => {
    if (searchParams.get('checkout') === 'true' && cart.length > 0) {
      setSearchParams({}, { replace: true });
      if (user) { setCheckoutUser(user); setStep('address'); }
      else setStep('login');
    }
  }, [searchParams, cart.length, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoginDone = (u) => { login(u); setCheckoutUser(u); setStep('address'); window.scrollTo(0, 0); };
  const handleAddressDone = (u) => {
    setCheckoutUser(u);
    // Check if any cart item belongs to a category requiring delivery choice
    const cats = deliverySettings?.categories || [];
    const aboveThreshold = deliverySettings?.freeDeliveryThreshold && subtotal >= deliverySettings.freeDeliveryThreshold;
    const hasRegisteredCategory = cats.length > 0 && cart.some(item => cats.includes(item.category));
    if (!hasRegisteredCategory) {
      // No items from delivery categories - skip delivery entirely
      setDeliveryChoice(null);
      setStep('payment');
    } else if (aboveThreshold) {
      // Above threshold - auto-assign registered (free)
      setDeliveryChoice('registered');
      setStep('payment');
    } else {
      // Below threshold with registered category items - show popup
      setShowDeliveryPopup(true);
      setStep('payment');
    }
    window.scrollTo(0, 0);
  };

  const proceedToCheckout = () => {
    if (user) { setCheckoutUser(user); setStep('address'); }
    else setStep('login');
    window.scrollTo(0, 0);
  };

  // Calculate delivery fee
  const deliveryFee = (() => {
    if (!deliveryChoice || deliveryChoice === 'non-registered') return 0;
    if (!deliverySettings) return 0;
    if (deliverySettings.freeDeliveryThreshold && subtotal >= deliverySettings.freeDeliveryThreshold) return 0;
    return deliverySettings.deliveryFee || 0;
  })();
  const orderTotal = subtotal + deliveryFee;

  const handleSelectGateway = async (gw) => {
    setSelectedGateway(gw);
    setPayError('');
    if (gw === 'stripe') {
      try {
        const { data } = await api.post('/stripe/create-payment-intent', { total: orderTotal.toFixed(2) });
        const pubKey = gateways.stripe?.publishableKey;
        setStripePromise(loadStripe(pubKey));
        setStripeClientSecret(data.clientSecret);
      } catch (e) {
        setPayError(e.response?.data?.error || 'Failed to initialize Stripe.');
        setSelectedGateway(null);
      }
    }
  };

  const createPayPalOrder = async () => {
    setPayError('');
    console.log('[PAYMENT] PayPal: creating order...', { items: cart.length, total: orderTotal.toFixed(2) });
    try {
      const { data } = await api.post('/paypal/create-order', {
        items: cart.map(i => ({ name: i.name, price: i.price, qty: i.qty })),
        total: orderTotal.toFixed(2),
      });
      console.log('[PAYMENT] PayPal order created:', data.id);
      return data.id;
    } catch (e) { console.error('[PAYMENT] PayPal create error:', e.response?.data || e.message); throw new Error(e.response?.data?.error || 'Could not create PayPal order'); }
  };

  const onPayPalApprove = async (paypalData) => {
    console.log('[PAYMENT] PayPal: capturing order...', paypalData.orderID);
    try {
      const { data } = await api.post(`/paypal/capture-order/${paypalData.orderID}`);
      console.log('[PAYMENT] PayPal captured:', data);
      const fallbackOrderID = data.orderID || `ORD-PP-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (!data.orderID) console.warn('[ORDER] No orderID from PayPal capture, using fallback:', fallbackOrderID);
      const orderPayload = {
        orderID: fallbackOrderID,
        gateway: 'paypal',
        amount: data.amount || subtotal.toFixed(2),
        currency: data.currency || currency || 'EUR',
        customer: {
          name: checkoutUser?.name,
          mobile: checkoutUser?.mobile,
          email: checkoutUser?.email,
          address: checkoutUser?.address,
        },
        items: cart.map(i => ({ name: i.name, qty: i.qty, price: i.price, image: i.images?.[0] || i.image || '' })),
        delivery: { type: deliveryChoice || 'none', fee: deliveryFee, estimatedDate: new Date(Date.now() + ((deliveryChoice === 'non-registered' ? deliverySettings?.nonRegisteredMaxDays : deliverySettings?.registeredMaxDays) || 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] },
      };
      console.log('[ORDER] Posting to /orders:', orderPayload);
      try {
        const orderRes = await api.post('/orders', orderPayload);
        console.log('[ORDER] Order created successfully:', orderRes.data);
      } catch (orderErr) {
        console.error('[ORDER] Failed to save order after PayPal payment:', orderErr.response?.data || orderErr.message);
        setPayError('Payment successful but order save failed. Contact support with ID: ' + fallbackOrderID);
      }
      clearCart();
      setOrderSuccess({ ...data, orderID: fallbackOrderID });
      window.scrollTo(0, 0);
    } catch (e) {
      console.error('[ORDER] PayPal capture error:', e.response?.data || e.message);
      throw new Error(e.response?.data?.error || 'Could not capture payment');
    }
  };

  if (cart.length === 0 && !orderSuccess) {
    return (
      <div className="cart-page section">
        <div className="container">
          <div className="cart-empty">
            <div className="cart-empty-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything yet.</p>
            <Link to="/products" className="btn">Shop Now</Link>
          </div>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="cart-page section">
        <div className="container">
          <div className="order-success">
            <div className="order-success-icon">✅</div>
            <h2>Payment Successful!</h2>
            <p>Thank you, <strong>{checkoutUser?.name || orderSuccess.payer?.name?.given_name}</strong>! Your order has been placed.</p>
            <div className="order-success-details">
              <div className="order-detail-row"><span>Order ID</span><strong>{orderSuccess.orderID}</strong></div>
              <div className="order-detail-row"><span>Amount Paid</span><strong>{orderSuccess.currency} {orderSuccess.amount}</strong></div>
              {checkoutUser?.address?.line1 && (
                <div className="order-detail-row">
                  <span>Deliver to</span>
                  <strong>{checkoutUser.address.line1}, {checkoutUser.address.city}, {checkoutUser.address.country}</strong>
                </div>
              )}
              {deliverySettings && deliveryChoice && (
                <div className="order-detail-row">
                  <span>Expected Delivery</span>
                  <strong>{new Date(Date.now() + ((deliveryChoice === 'non-registered' ? deliverySettings?.nonRegisteredMaxDays : deliverySettings?.registeredMaxDays) || 7) * 24 * 60 * 60 * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                </div>
              )}
            </div>
            <p className="order-success-note">We'll contact you on WhatsApp / email with delivery updates. 🚚</p>
            <Link to="/products" className="btn">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'login') return (
    <div className="cart-page section">
      <div className="container checkout-container">
        <LoginStep onLogin={handleLoginDone} />
      </div>
    </div>
  );

  if (step === 'address') return (
    <div className="cart-page section">
      <div className="container checkout-container">
        <AddressStep user={checkoutUser} onSave={handleAddressDone} onBack={() => setStep('cart')} />
      </div>
    </div>
  );


  if (step === 'payment') return (
    <>
      {showDeliveryPopup && (
        <div className="delivery-popup-overlay">
          <div className="delivery-popup">
            <h3>Choose Delivery Method</h3>
            <div className="delivery-options">
              <label className={`delivery-option${deliveryChoice === 'registered' ? ' selected' : ''}`}>
                <input type="radio" name="delivery" value="registered" checked={deliveryChoice === 'registered'} onChange={() => setDeliveryChoice('registered')} />
                <div className="delivery-option-info">
                  <strong>Registered Delivery with An Post</strong>
                  {deliverySettings?.freeDeliveryThreshold && subtotal >= deliverySettings.freeDeliveryThreshold
                    ? <span className="delivery-price"><s>{currency}{(deliverySettings.deliveryFee || 0).toFixed(2)}</s> <span className="delivery-free">FREE</span></span>
                    : <span className="delivery-price">{currency}{(deliverySettings?.deliveryFee || 0).toFixed(2)}</span>
                  }
                  {deliverySettings?.registeredMaxDays && <small>Est. delivery by: {new Date(Date.now() + deliverySettings.registeredMaxDays * 24 * 60 * 60 * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</small>}
                  <small className="delivery-description">The item will be sent via registered post with An Post, using their next working day delivery service. A tracking number will be provided once the parcel has been dispatched.</small>
                </div>
              </label>
              <label className={`delivery-option${deliveryChoice === 'non-registered' ? ' selected' : ''}`}>
                <input type="radio" name="delivery" value="non-registered" checked={deliveryChoice === 'non-registered'} onChange={() => setDeliveryChoice('non-registered')} />
                <div className="delivery-option-info">
                  <strong>Non-Registered Delivery with An Post</strong>
                  <span className="delivery-price"><s>{currency}{(deliverySettings?.deliveryFee || 0).toFixed(2)}</s> <span className="delivery-free">FREE</span></span>
                  {deliverySettings?.nonRegisteredMaxDays && <small>Est. delivery by: {new Date(Date.now() + deliverySettings.nonRegisteredMaxDays * 24 * 60 * 60 * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</small>}
                  <small className="delivery-description">Enjoy free standard delivery with An Post. Your order will arrive in 3–5 working days.</small>
                </div>
              </label>
            </div>
            <button className="btn checkout-btn" disabled={!deliveryChoice} onClick={() => setShowDeliveryPopup(false)}>Continue to Payment</button>
          </div>
        </div>
      )}
      <PaymentStep
        checkoutUser={checkoutUser}
        cart={cart}
        subtotal={orderTotal}
        deliveryFee={deliveryFee}
        deliveryChoice={deliveryChoice}
        deliverySettings={deliverySettings}
        itemSubtotal={subtotal}
        currency={currency}
        gateways={gateways}
        paypalEnabled={paypalEnabled}
        stripeEnabled={stripeEnabled}
        selectedGateway={selectedGateway}
        setSelectedGateway={setSelectedGateway}
        stripePromise={stripePromise}
        stripeClientSecret={stripeClientSecret}
        payError={payError}
        setPayError={setPayError}
        handleSelectGateway={handleSelectGateway}
        createPayPalOrder={createPayPalOrder}
        onPayPalApprove={onPayPalApprove}
        clearCart={clearCart}
        setOrderSuccess={setOrderSuccess}
        setStripeClientSecret={setStripeClientSecret}
        onBack={() => setStep('address')}
        onEditAddress={() => setStep('address')}
      />
    </>
  );

  // ── Cart view ──
  return (
    <div className="cart-page section">
      <div className="container">
        <div className="cart-header-row">
          <h1>Your Cart</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {user && (
              <>
                <Link to="/orders" className="cart-user-tag" style={{ textDecoration: 'none' }}>
                  📦 My Orders
                </Link>
                <span className="cart-user-tag">👤 {user.name} <button onClick={logout} className="cart-logout-btn">Sign out</button></span>
              </>
            )}
            <button className="cart-clear-btn" onClick={clearCart}>Clear Cart</button>
          </div>
        </div>

        {syncNotes.length > 0 && (
          <div className="cart-sync-banner">
            <div className="cart-sync-banner-body">
              <span className="cart-sync-icon">⚠️</span>
              <div>
                <strong>Your cart was updated</strong>
                <ul className="cart-sync-list">
                  {syncNotes.map((n, i) => (
                    <li key={i}>
                      {n.type === 'removed'
                        ? <><strong>{n.name}</strong> was removed — currently out of stock.</>
                        : <><strong>{n.name}</strong> price changed from {currency}{parseFloat(n.oldPrice).toFixed(2)} to {currency}{parseFloat(n.newPrice).toFixed(2)}.</>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <button className="cart-sync-dismiss" onClick={dismissSyncNotes}>✕</button>
          </div>
        )}

        <div className="cart-layout">
          <div className="cart-items">
            {cart.map(item => (
              <div key={item._id} className="cart-item">
                <div className="cart-item-img">
                  <img src={getImageUrl(item.images?.[0] || item.image)} alt={item.name} onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
                </div>
                <div className="cart-item-info">
                  <h3>{item.name}</h3>
                  {item.brand && <span className="cart-item-brand">{item.brand}</span>}
                  <span className="cart-item-price">{currency}{item.price.toFixed(2)}</span>
                </div>
                <div className="cart-item-qty">
                  <button onClick={() => updateQty(item._id, item.qty - 1)}>−</button>
                  <span>{item.qty}</span>
                  <button onClick={() => updateQty(item._id, item.qty + 1)}>+</button>
                </div>
                <div className="cart-item-subtotal">{currency}{(item.price * item.qty).toFixed(2)}</div>
                <button className="cart-item-remove" onClick={() => removeFromCart(item._id)}>✕</button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="cart-summary-row"><span>Subtotal</span><span>{currency}{subtotal.toFixed(2)}</span></div>
            <div className="cart-summary-row"><span>Shipping</span><span className="cart-free">Free</span></div>
            <div className="cart-summary-row cart-summary-total"><span>Total</span><span>{currency}{subtotal.toFixed(2)}</span></div>
            <button className="btn checkout-btn" onClick={proceedToCheckout}>
              {user ? 'Proceed to Checkout' : 'Sign In & Checkout'}
            </button>
            <Link to="/products" className="cart-continue-link">← Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;
